<template>
    <RegularModal ref="modal">
        <template v-slot:title>{{ translate("event_ranking.specify_rules_short") }}</template>
        <template v-slot:body>
            <div class="columns">
                <div class="column">
                    <label class="label">{{ translate("event_ranking.select_categories") }}</label>
                    <QueryInput :size="'is-small'" class="mr-3" v-model="categoriesQuery" />

                    <SelectionList
                        ref="categoriesSelectionList"
                        :options="filteredCategories"
                        :multi-select="true"
                        v-model="selectedCategories"
                        :comparing-function="(a: Category, b: Category) => a.key === b.key"
                    >
                        <template v-slot:option="{ option: category }">
                            <div class="option">
                                {{ category.value }}
                            </div>
                        </template>
                    </SelectionList>

                    <div class="buttons mt-2">
                        <button class="button is-small" @click="selectVisibleCategories">
                            {{ translate("page.admin_academy_results.select_visible") }}
                        </button>
                        <button class="button is-small" @click="deselectVisibleCategories">
                            {{ translate("page.admin_academy_results.unselect_visible") }}
                        </button>
                    </div>
                </div>
            </div>

            <div class="columns">
                <div class="column">
                    <label class="label"> {{ translate("event_ranking.rules") }} </label>

                    <div class="field">
                        <div class="control">
                            <div class="select">
                                <select v-model="selectedRule">
                                    <option :value="null">---</option>
                                    <option v-for="availableRule in availableRules" :value="availableRule" :key="availableRule">
                                        {{ translateRule(availableRule) }}
                                    </option>
                                </select>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="columns">
                <div class="column">
                    <template v-if="selectedRule === Rule.AwardPointsForWin">
                        <AwardPointsForWinningMatchRule ref="rule" />
                    </template>
                    <Notification v-else-if="availableRules.length > 0" class="is-info">
                        <span class="icon">
                            <i class="fas fa-info-circle"></i>
                        </span>
                        <span>{{ translate("event_ranking.please_add_rule") }}</span>
                    </Notification>
                    <Notification v-else class="is-warning">
                        <span class="icon">
                            <i class="fas fa-exclamation-triangle"></i>
                        </span>
                        <span>{{ translate("event_ranking.no_more_rules_to_add") }}</span>
                    </Notification>
                </div>
            </div>
        </template>
        <template v-slot:footer>
            <div class="buttons">
                <button class="button" @click="close">{{ translate("common.cancel") }}</button>
                <LoadingButton :is-loading="savingInProgress" class="button is-primary" @click="saveRule" :disabled="!canBeRuleAdded">
                    {{ translate("common.save") }}
                </LoadingButton>
            </div>
        </template>
    </RegularModal>
</template>

<script setup lang="ts">
    import RegularModal from "../../Common/RegularModal.vue";
    import { useModal } from "../../Common/useModal";
    import { computed, ref } from "vue";
    import AwardPointsForWinningMatchRule from "./AwardPointsForWinningMatchRule.vue";
    import { useEventRankingsStore } from "../eventRankingsStore";
    import Notification from "../../Common/Notification.vue";
    import { useDefineRuleStore } from "./defineRuleStore";
    import { RuleDefinition, translateRule } from "./types";
    import { translate } from "../../Common/translations";
    import SelectionList from "../../Common/SelectionList.vue";
    import QueryInput from "../../Common/QueryInput.vue";
    import { Category } from "../../Common/types";
    import objectMatcher from "../../Common/objectMatcher";
    import { ComponentExposed } from "vue-component-type-helpers";
    import categoriesApi from "../../Api/Categories/categories";
    import LoadingButton from "../../Common/LoadingButton.vue";
    import { handleError } from "../../Common/errorHandler";

    const { modal, open: openModal, close } = useModal();
    const store = useEventRankingsStore();
    const categoriesSelectionList = ref<ComponentExposed<typeof SelectionList> | null>(null);
    const defineRuleStore = useDefineRuleStore();

    function open() {
        selectedRule.value = null;
        selectedCategories.value = [];
        categoriesQuery.value = "";

        openModal();

        return categoriesApi.getCategories(store.eventId).then(resp => {
            categories.value = resp;
        });
    }

    defineExpose({ open });

    // SELECTING CATEGORIES
    const selectedCategories = ref<Category[]>([]);
    const categoriesQuery = ref("");
    const categories = ref<Category[]>([]);
    const filteredCategories = computed<Category[]>(() => {
        return categories.value.filter(category => {
            return objectMatcher.matchString(categoriesQuery.value, category.value);
        });
    });

    function selectVisibleCategories() {
        categoriesSelectionList.value?.selectAllVisible();
    }
    function deselectVisibleCategories() {
        categoriesSelectionList.value?.deselectAllVisible();
    }

    const selectedCategoriesArray = computed(() => {
        return selectedCategories.value.map(category => category.key);
    });

    // SELECTING RULES

    const Rule = {
        AwardPointsForWin: "awardPointsForWin",
    } as const;
    type RuleType = (typeof Rule)[keyof typeof Rule];

    const availableRules = computed(() => {
        return Object.values(Rule);
    });
    const selectedRule = ref<RuleType | null>(null);

    const canBeRuleAdded = computed(() => {
        return selectedCategories.value.length > 0 && selectedRule.value !== null && defineRuleStore.selectedRuleValid;
    });

    // SAVING RULES TO STORE

    const rule = ref();
    const savingInProgress = ref(false);

    function saveRule() {
        if (!canBeRuleAdded.value) {
            return;
        }
        const ruleToSave: RuleDefinition = {
            ...rule.value.getRuleDefinition(),
            //TODO Fix it, this is a workaround. getRuleDefinition() should give contract with selected categories and at the same time return empty array.
            selectedCategories: selectedCategoriesArray.value,
        };
        store.definedRules.push(ruleToSave);
        store.persistInLocalStorage();
        savingInProgress.value = true;

        return store
            .refreshRanking()
            .then(() => {
                close();
            })
            .catch(handleError)
            .finally(() => {
                savingInProgress.value = false;
            });
    }
</script>

<style scoped></style>
